import React, { useState } from "react";
import "../../../../styles/components/general/elements/inputElements/selectInputElementComponent.scss";

export const SelectInputElementComponent = ({ props }) => {
  const {
    title,
    label,
    name,
    value,
    options,
    onChange,
    required,
    clearBaseDesign,
    customStyleClass,
    disabled,
    searchable = false, // New prop to enable search
    multiple = false, // 0011: opt-in multi-select mode (see onChange contract below)
    searchableList = false, // 0023: opt-in single-select inline-list mode (see onChange contract below)
    selectionLabel, // 0012: opt-in; when set (multi mode) renders a "<selectionLabel> (N)" counter
    placeholder = "חפש...",
  } = props;

  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const filteredOptions = options?.filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()) || (option.subOptions && option.subOptions.some((sub) => sub.label.toLowerCase().includes(searchQuery.toLowerCase()))));

  // 0011: Multi-select mode. NOTE the differing onChange contract:
  //  - single mode (default): onChange receives the native <select> change EVENT.
  //  - multiple mode:         onChange receives the new ARRAY of selected values.
  // The parent owns the array state. value is treated as [] when undefined here.
  if (multiple) {
    const selectedValues = Array.isArray(value) ? value : [];
    const isSelected = (optValue) => selectedValues.some((v) => String(v) === String(optValue));

    const toggleValue = (optValue) => {
      if (disabled) return;
      const next = isSelected(optValue) ? selectedValues.filter((v) => String(v) !== String(optValue)) : [...selectedValues, optValue];
      onChange(next);
    };

    const selectedOptions = options?.filter((option) => isSelected(option.value)) || [];

    return (
      <div className={`${clearBaseDesign ? "" : "select-element-container"} select-multi-container ${customStyleClass || ""}`}>
        {/* 0012: when selectionLabel is provided, the top label doubles as a live count. */}
        {selectionLabel ? (
          <label htmlFor={name} className="select-multi-count">
            {selectionLabel} ({selectedValues.length})
          </label>
        ) : (
          <label htmlFor={name}>{title ? title : label}</label>
        )}

        {selectedOptions.length > 0 && (
          <div className="select-multi-chips">
            {selectedOptions.map((option) => (
              <span key={option.value} className="select-multi-chip">
                {option.label}
                <button type="button" className="select-multi-chip-remove" aria-label="הסר" onClick={() => toggleValue(option.value)} disabled={disabled}>
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <input
          type="text"
          id={name}
          name={name}
          className="search-input"
          placeholder={placeholder}
          value={searchQuery}
          disabled={disabled}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <div className="select-multi-options">
          {filteredOptions?.length ? (
            filteredOptions.map((option) => (
              <div key={option.value} className={`select-multi-option ${isSelected(option.value) ? "is-selected" : ""}`} onClick={() => toggleValue(option.value)}>
                <input type="checkbox" readOnly checked={isSelected(option.value)} tabIndex={-1} />
                <span>{option.label}</span>
              </div>
            ))
          ) : (
            <div className="select-multi-empty">לא נמצאו תוצאות</div>
          )}
        </div>
      </div>
    );
  }

  // 0023: single-select inline-list mode. Mirrors the `multiple` branch above (filter input +
  // inline scrollable <div> of clickable rows) but in single-select form: clicking a row picks it.
  // NO native <select> and NO OS popup — so the list scrolls WITHIN its container and can never clip
  // against the 0017 bar inside a dialog (the whole reason this mode exists — see spec 0023 §R5).
  // onChange contract: like the multi branch, onChange receives the picked option's VALUE (not the
  // native change EVENT). The parent maps that value back to its domain object.
  if (searchableList) {
    const isSelected = (optValue) => String(value) === String(optValue);

    const pickValue = (optValue) => {
      if (disabled) return;
      onChange(optValue);
    };

    return (
      <div className={`${clearBaseDesign ? "" : "select-element-container"} select-list-container ${customStyleClass || ""}`}>
        <label htmlFor={name}>{title ? title : label}</label>

        <input
          type="text"
          id={name}
          name={name}
          className="search-input"
          placeholder={placeholder}
          value={searchQuery}
          disabled={disabled}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <div className="select-list-options">
          {filteredOptions?.length ? (
            filteredOptions.map((option) => (
              <div key={option.value} className={`select-list-option ${isSelected(option.value) ? "is-selected" : ""}`} onClick={() => pickValue(option.value)}>
                <span>{option.label}</span>
              </div>
            ))
          ) : (
            <div className="select-list-empty">לא נמצאו תוצאות</div>
          )}
        </div>
      </div>
    );
  }

  const getSelectElementOptions = () => {
    const elementOptions = [];
    elementOptions.push(
      <option key={-1} value="">
        בחר {label}
      </option>
    );
    filteredOptions?.forEach((option) => {
      if (option?.subOptions && option?.subOptions?.length > 0) {
        elementOptions.push(
          <optgroup key={option.value} label={option.label}>
            {option.subOptions?.map((subOption) => (
              <option key={subOption.value} value={subOption.value}>
                {subOption.label}
              </option>
            ))}
          </optgroup>
        );
      } else {
        elementOptions.push(
          <option key={option?.value} value={option?.value}>
            {option?.label}
          </option>
        );
      }
    });

    return elementOptions;
  };

  return (
    <div className={`${clearBaseDesign ? "" : "select-element-container"} ${customStyleClass || ""}`}>
      <label htmlFor={name}>{title ? title : label}</label>
      {searchable && (
        <input
          type="text"
          className="search-input"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsDropdownOpen(true)}
          onBlur={() => setTimeout(() => setIsDropdownOpen(false), 150)} // Delay to allow item selection
        />
      )}
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        size={isDropdownOpen ? Math.min(filteredOptions?.length + 1, 5) : 1} // Limit dropdown size
      >
        {getSelectElementOptions()}
      </select>
    </div>
  );
};

export default SelectInputElementComponent;

// import React from "react";
// import "../../../../styles/components/general/elements/inputElements/selectInputElementComponent.scss";

// export const SelectInputElementComponent = ({ props }) => {
//   const { title, label, name, value, options, onChange, required, clearBaseDesign, customStyleClass, disabled } = props;

//   const getSelectElementOptions = () => {
//     const elementOptions = [];
//     elementOptions.push(
//       <option key={-1} value="">
//         בחר {label}
//       </option>
//     );
//     options?.forEach((option) => {
//       if (option?.subOptions && option?.subOptions?.length > 0) {
//         elementOptions.push(
//           <optgroup key={option.value} label={option.label}>
//             {option.subOptions?.map((subOption) => (
//               <option key={subOption.value} value={subOption.value}>
//                 {subOption.label}
//               </option>
//             ))}
//           </optgroup>
//         );
//       } else {
//         elementOptions.push(
//           <option key={option?.value} value={option?.value}>
//             {option?.label}
//           </option>
//         );
//       }
//     });

//     return elementOptions;
//   };

//   return (
//     <div className={`${clearBaseDesign ? "" : "select-element-container"} ${customStyleClass ? customStyleClass : ""}`}>
//       <label htmlFor={name}>{title ? title : label}</label>
//       <select id={name} name={name} value={value} onChange={onChange} required={required} disabled={disabled}>
//         {getSelectElementOptions()}
//       </select>
//     </div>
//   );
// };

// export default SelectInputElementComponent;
